import { useState, FormEvent } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import ConsultModal from '../../components/feature/ConsultModal';
import ScrollToTop from '../../components/feature/ScrollToTop';
import SeoHead from '../../components/feature/SeoHead';
import { useTranslation } from 'react-i18next';
import { getLangPath, type Language } from '../../router/langRoutes';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://thedaeg.com';

const seoData: Record<string, { title: string; description: string; keywords: string }> = {
  vi: {
    title: 'Liên Hệ Tư Vấn Định Cư Latvia - The Daeg International | Hotline & Email',
    description:
      'Liên hệ The Daeg International để được tư vấn miễn phí về định cư Latvia. Hotline, email, địa chỉ văn phòng tại Riga, Latvia. Đội ngũ chuyên gia định cư Latvia sẵn sàng hỗ trợ người Việt Nam 24/7.',
    keywords:
      'liên hệ định cư Latvia, tư vấn định cư Latvia, hotline định cư Latvia, email tư vấn định cư, văn phòng The Daeg Latvia, tư vấn miễn phí định cư',
  },
  en: {
    title: 'Contact Us - The Daeg International | Latvia Residency Consultation',
    description:
      'Contact The Daeg International for free Latvia residency consultation. Hotline, email, office address in Riga, Latvia. Expert team ready to support Vietnamese clients.',
    keywords:
      'contact The Daeg, Latvia residency consultation, Latvia immigration hotline, Riga office contact',
  },
  lv: {
    title: 'Sazināties - The Daeg International | Latvijas Uzturēšanās Konsultācija',
    description:
      'Sazinieties ar The Daeg International, lai saņemtu bezmaksas konsultāciju par uzturēšanās atļauju Latvijā. Tālrunis, e-pasts, biroja adrese Rīgā.',
    keywords:
      'sazināties The Daeg, Latvijas uzturēšanās konsultācija, Rīgas birojs',
  },
};

const ContactPage = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { t, i18n } = useTranslation('contact');
  const lang = (i18n.language in seoData ? i18n.language : 'vi') as Language;
  const seo = seoData[lang];

  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    subject: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const contactPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'ContactPage',
    name: seo.title,
    description: seo.description,
    url: `${SITE_URL}${getLangPath('contact', lang)}`,
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: t('breadcrumb.home'), item: `${SITE_URL}${getLangPath('home', lang)}` },
        { '@type': 'ListItem', position: 2, name: t('breadcrumb.contact'), item: `${SITE_URL}${getLangPath('contact', lang)}` },
      ],
    },
  };

  const localBusinessSchema = {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    name: 'The Daeg International Trade',
    url: SITE_URL,
    telephone: '+371-2572-6969',
    email: 'contact@thedaeg.com',
    address: {
      '@type': 'PostalAddress',
      streetAddress: 'Krisjana Barona Iela 96/98',
      addressLocality: 'Riga',
      addressCountry: 'LV',
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: 56.9496,
      longitude: 24.1052,
    },
    openingHoursSpecification: [
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
        opens: '09:00',
        closes: '18:00',
      },
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: ['Saturday'],
        opens: '09:00',
        closes: '12:00',
      },
    ],
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('phone', formData.phone);
      formBody.append('email', formData.email);
      formBody.append('subject', formData.subject);
      formBody.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d6dhh53v53dtlm7j8jv0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formBody.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', phone: '', email: '', subject: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactItems = [
    {
      icon: 'ri-map-pin-line',
      title: t('info.address.label'),
      lines: [t('info.address.value'), t('info.address.line2')],
    },
    {
      icon: 'ri-phone-line',
      title: t('info.phone.label'),
      lines: [t('info.phone.display'), ''],
    },
    {
      icon: 'ri-mail-line',
      title: t('info.email.label'),
      lines: [t('info.email.value'), ''],
    },
    {
      icon: 'ri-time-line',
      title: t('info.hours.label'),
      lines: [t('info.hours.value'), t('info.hours.saturday')],
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <SeoHead
        title={seo.title}
        description={seo.description}
        keywords={seo.keywords}
        canonicalPath={getLangPath('contact', lang)}
        schemaJson={[contactPageSchema, localBusinessSchema]}
      />
      <Navbar onConsultClick={() => setIsModalOpen(true)} />

      {/* Hero */}
      <section className="relative pt-20 md:pt-32 pb-8 md:pb-20 bg-gradient-to-br from-gray-900 via-black to-gray-900 text-white">
        <div className="absolute inset-0 opacity-20">
          <img
            src="https://public.readdy.ai/ai/img_res/b5a39c5c09303ac76c56e4e02cd8904c.jpg"
            alt={t('hero.title')}
            className="w-full h-full object-cover object-top"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 to-black/80"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-2xl md:text-5xl lg:text-6xl font-bold mb-2 md:mb-6 font-serif">{t('hero.title')}</h1>
          <p className="text-xs md:text-xl text-gray-300 max-w-3xl">{t('hero.subtitle')}</p>
        </div>
      </section>

      {/* Main content */}
      <section className="py-8 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-12">

            {/* Left: contact info */}
            <div>
              <h2 className="text-xl md:text-3xl font-bold text-gray-900 mb-2 md:mb-4 font-serif">{t('info.title')}</h2>
              <p className="text-xs md:text-base text-gray-600 mb-4 md:mb-8 leading-relaxed">
                {t('info.description')}
              </p>
              <div className="space-y-3 md:space-y-6">
                {contactItems.map((item, i) => (
                  <div key={i} className="flex items-start">
                    <div className="w-8 h-8 md:w-12 md:h-12 flex items-center justify-center bg-gold-100 rounded-lg mr-2.5 md:mr-4 flex-shrink-0">
                      <i className={`${item.icon} text-base md:text-2xl text-gold-600`}></i>
                    </div>
                    <div>
                      <h3 className="text-sm md:text-lg font-bold text-gray-900 mb-0.5">{item.title}</h3>
                      {item.lines.map((line, j) => line ? <p key={j} className="text-[11px] md:text-sm text-gray-600">{line}</p> : null)}
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-4 md:mt-8">
                <h3 className="text-sm md:text-lg font-bold text-gray-900 mb-2 md:mb-4">{t('social.title')}</h3>
                <div className="flex space-x-2.5 md:space-x-4">
                  {['ri-facebook-fill', 'ri-linkedin-fill', 'ri-youtube-fill', 'ri-instagram-line'].map((icon, i) => (
                    <a key={i} href="#" rel="nofollow"
                      className="w-9 h-9 md:w-12 md:h-12 flex items-center justify-center bg-gray-100 rounded-full hover:bg-gold-600 hover:text-white transition-colors cursor-pointer">
                      <i className={`${icon} text-base md:text-xl`}></i>
                    </a>
                  ))}
                </div>
              </div>
            </div>

            {/* Right: form */}
            <div className="bg-gray-50 rounded-xl md:rounded-2xl p-4 md:p-8">
              <h2 className="text-lg md:text-3xl font-bold text-gray-900 mb-3 md:mb-6 font-serif">{t('form.title')}</h2>
              <form id="contact-form" data-readdy-form onSubmit={handleSubmit} className="space-y-2.5 md:space-y-4">
                <div>
                  <label htmlFor="name" className="block text-[11px] md:text-sm font-medium text-gray-700 mb-0.5 md:mb-1">
                    {t('form.labels.fullName')} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text" id="name" name="name" required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-3 py-2 md:px-4 md:py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent text-xs md:text-sm"
                    placeholder={t('form.placeholders.fullName')}
                  />
                </div>

                <div className="grid grid-cols-2 gap-2.5 md:gap-4">
                  <div>
                    <label htmlFor="phone" className="block text-[11px] md:text-sm font-medium text-gray-700 mb-0.5 md:mb-1">
                      {t('form.labels.phone')} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="tel" id="phone" name="phone" required
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="w-full px-3 py-2 md:px-4 md:py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent text-xs md:text-sm"
                      placeholder={t('form.placeholders.phone')}
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-[11px] md:text-sm font-medium text-gray-700 mb-0.5 md:mb-1">
                      {t('form.labels.email')} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email" id="email" name="email" required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full px-3 py-2 md:px-4 md:py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent text-xs md:text-sm"
                      placeholder={t('form.placeholders.email')}
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="subject" className="block text-[11px] md:text-sm font-medium text-gray-700 mb-0.5 md:mb-1">
                    {t('form.labels.subject')} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text" id="subject" name="subject" required
                    value={formData.subject}
                    onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                    className="w-full px-3 py-2 md:px-4 md:py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent text-xs md:text-sm"
                    placeholder={t('form.placeholders.subject')}
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-[11px] md:text-sm font-medium text-gray-700 mb-0.5 md:mb-1">
                    {t('form.labels.message')} <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    id="message" name="message" rows={3} maxLength={500} required
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    className="w-full px-3 py-2 md:px-4 md:py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent text-xs md:text-sm resize-none"
                    placeholder={t('form.placeholders.message')}
                  ></textarea>
                  <p className="text-[10px] md:text-xs text-gray-500 mt-0.5">
                    {formData.message.length}/500 {t('form.charCount')}
                  </p>
                </div>

                {submitStatus === 'success' && (
                  <div className="p-2 md:p-3 bg-green-50 border border-green-200 rounded-lg text-[11px] md:text-sm text-green-700">
                    {t('form.success')}
                  </div>
                )}
                {submitStatus === 'error' && (
                  <div className="p-2 md:p-3 bg-red-50 border border-red-200 rounded-lg text-[11px] md:text-sm text-red-700">
                    {t('form.error')}
                  </div>
                )}

                <button
                  type="submit" disabled={isSubmitting}
                  className="w-full px-5 py-2.5 md:px-6 md:py-3 bg-gold-600 text-white text-xs md:text-base font-semibold rounded-lg hover:bg-gold-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer">
                  {isSubmitting ? t('form.submitting') : t('form.submit')}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Map */}
      <section className="py-0 bg-white">
        <div className="w-full h-48 md:h-96">
          <iframe
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2175.5234567890123!2d24.1051846!3d56.9496487!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNTbCsDU2JzU4LjciTiAyNMKwMDYnMTguNyJF!5e0!3m2!1sen!2slv!4v1234567890123!5m2!1sen!2slv"
            width="100%" height="100%" style={{ border: 0 }} allowFullScreen loading="lazy"
            referrerPolicy="no-referrer-when-downgrade" title="The Daeg International Location"
          ></iframe>
        </div>
      </section>

      <Footer />
      <ConsultModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
      <ScrollToTop />
    </div>
  );
};

export default ContactPage;
